import React from 'react'
import { graphql } from 'gatsby'
import { BREAKPOINT, COLOR } from '@farewill/ui/tokens'
import { Divider, Wrapper } from '@farewill/ui'

import PATHS from 'paths'
import { TRUSTPILOT_RATING_OUT_OF_5, TRUSTPILOT_REVIEWS_TOTAL } from 'config'
import { PRODUCTS } from 'lib/products/constants'
import { TRACKING_TYPES } from 'lib/tracking/constants'
import { roundDownToNearestHundred } from 'lib/formatting/numbers'
import DefaultLayout from 'layouts/DefaultLayout'
import BreakpointSwitch from 'components/BreakpointSwitch'
import GetStarted from 'views/wills/components/Overview/GetStarted'
import Header from '../slices/Header'

import Testimonial from '../slices/Testimonial'
import Hero from '../slices/Hero'
import Trustpilot from '../slices/Trustpilot'
import FAQ from '../slices/FAQ'
import WillOptions from '../slices/WillOptions'
import About from '../slices/About'

import type { BespokeJourneyProps } from '../types'
import MessageBanner from '../slices/MessageBanner'
import { CRUK_ACCENT_COLOR, CRUK_MESSAGE_BACKGROUND_COLOR } from '../constants'
import PartnerBenefit from '../slices/PartnerBenefit'

const BespokeJourney = ({
  data: {
    page: {
      utmCampaign,
      utmMedium,
      partner,
      heroTitle,
      heroSubtitle,
      heroCtaText,
    },
    faqSection,
  },
}: BespokeJourneyProps): React.ReactElement => (
  <DefaultLayout
    branding={partner.utmSource}
    title="Online Will Writing Service | Legal Will In 15 Minutes"
    description={`Our solicitor-approved guide helps you write a will online from the comfort of your own home. Rated ${TRUSTPILOT_RATING_OUT_OF_5} out of 5 by over ${roundDownToNearestHundred(
      TRUSTPILOT_REVIEWS_TOTAL
    )} customers on Trustpilot. National Will Writing Firm of the Year.`}
    meta={[
      {
        name: 'robots',
        content: 'noindex',
      },
    ]}
    customHeader={<Header />}
    product={PRODUCTS.WILLS}
    canonicalPath={PATHS.WILLS.OVERVIEW}
    headerColor={COLOR.WHITE}
    footerType="compact"
    utmParameters={{
      partnerId: partner?.partnerId || TRACKING_TYPES.NOT_SET,
      utmCampaign: utmCampaign || TRACKING_TYPES.NOT_SET,
      utmMedium: utmMedium || TRACKING_TYPES.NOT_SET,
      utmSource: partner?.utmSource || TRACKING_TYPES.NOT_SET,
      utmChannel: partner?.utmChannel || TRACKING_TYPES.NOT_SET,
    }}
  >
    <Wrapper background={COLOR.WHITE}>
      <Hero
        assetDirectory="bespoke-journey-cruk"
        title={heroTitle || 'Write your Will your way'}
        subtitle={
          heroSubtitle?.heroSubtitle ??
          'Farewill have partnered with Cancer Research UK to make Will-writing easier for you. You can write or update a simple Will, and Cancer Research UK will pick up the bill.'
        }
        ctaText={heroCtaText || 'Let’s get started'}
        minHeight={650}
        imageTransformationParams={{
          small: 'ar_1.8,c_fill,f_auto,q_auto,g_faces,w_600',
          medium: 'ar_2.23,c_fill,f_auto,q_auto,g_faces,w_964',
          large: 'ar_1.05,c_fill,f_auto,q_auto,g_faces,h_650',
          extraLarge: 'ar_1.8,c_crop,f_auto,q_auto,g_faces,z_0.8/h_800',
        }}
        telephoneWillsEnabled={false}
      />

      <MessageBanner
        partnerName="Cancer Research UK"
        backgroundColor={CRUK_MESSAGE_BACKGROUND_COLOR}
        titleColor={CRUK_ACCENT_COLOR}
      >
        If you use our service, we hope that, in return, you’ll choose to leave
        us a gift in your Will so we can continue to fund life-saving research.
        But there’s no obligation. It’s your choice.
      </MessageBanner>

      <WillOptions
        partnerName="Cancer Research UK"
        telephoneWillsEnabled={false}
      />
      <About />
      <Testimonial />

      <PartnerBenefit />

      <BreakpointSwitch
        breakAt={BREAKPOINT.S}
        aboveComponent={<></>}
        belowComponent={<Divider />}
      />

      <Wrapper container noTrim>
        <Trustpilot
          tag="CRUK"
          title="Farewill is trusted by Cancer Research UK supporters"
        />
        <BreakpointSwitch breakAt={BREAKPOINT.M} aboveComponent={<Divider />} />
        <FAQ
          questions={faqSection.questions}
          imagePath="com-assets/bespoke-journey-cruk/circle-illustration-4-faq"
        />
      </Wrapper>

      <GetStarted
        partnerCampaignReferral
        telephoneWillsEnabled={false}
        title="Ready to write your will?"
      />
    </Wrapper>
  </DefaultLayout>
)

export const query = graphql`
  query CrukBespokePartnerJourney($slug: String!) {
    page: contentfulPartnerCampaignPage(slug: { eq: $slug }) {
      utmCampaign
      utmMedium
      heroTitle
      heroSubtitle {
        heroSubtitle
      }
      heroCtaText
      partner {
        partnerId
        telephoneWillsEnabled
        utmSource
        utmChannel
      }
      telephoneWillsEnabled
    }
    faqSection: contentfulFaqSection(
      contentful_id: { eq: "7tocoTSkp7dYTP2Pteki7Q" }
    ) {
      ...FaqSectionFields
    }
  }
`

export default BespokeJourney
